import React, { useRef, useMemo } from 'react'
import { useFrame } from '@react-three/fiber'
import { useGLTF } from '@react-three/drei'
import * as THREE from 'three'
import { useGenesisState } from '../hooks/useGenesisState'

interface TitanCoreProps {
  position?: [number, number, number]
  scale?: number
  beamActive?: boolean
}

/**
 * TitanCore - Aether Void Visualization
 *
 * "Aether Void" - Pure cosmic emptiness where divine light prevails:
 * - Pure void black (#000000) - the infinite cosmos
 * - Neon blue (#00FFFF) - cosmic dust particles, brand identity
 * - Deep gold (#D4AF37) - Ophanim radiance
 *
 * ONLY geometry: Ophanim GLB model + Divine Eye + 500K neon blue particles
 * ONLY light sources: Ophanim gold + Eye glow + neon blue particle glow
 */
export const TitanCore: React.FC<TitanCoreProps> = ({
  position = [0, 0, -35],
  scale = 8,
  beamActive = false
}) => {
  const isPortalOpen = useGenesisState(s => s.isPortalOpen)
  const ringSpeed = useGenesisState(s => s.ringSpeed)
  const setLastObservation = useGenesisState(s => s.setLastObservation)
  const coreRef = useRef<THREE.Group>(null)
  const particleStreamRef = useRef<THREE.Points>(null)
  const ophanimRef = useRef<THREE.Group>(null)
  const divineEyeRef = useRef<THREE.Group>(null)
  const irisRef = useRef<THREE.Mesh>(null)
  const pupilRef = useRef<THREE.Mesh>(null)
  const scleraRef = useRef<THREE.Mesh>(null)
  
  // Load the Ophanim GLB model
  const { scene: ophanimModel } = useGLTF('/models/ophanim_angel.glb')
  
  // Clone the model for reuse and apply materials
  const ophanimMesh = useMemo(() => {
    const cloned = ophanimModel.clone()
    
    // Traverse and apply divine golden/beryl materials
    cloned.traverse((child) => {
      if ((child as THREE.Mesh).isMesh) {
        const mesh = child as THREE.Mesh;
        
        // Hide the GLB eye assembly — it's at a baked offset outside the ring centre.
        // We replace it with a custom divine eye positioned exactly at (0,0,0).
        const isEyeComponent = mesh.name === 'Sphere001_Material006_0'
          || mesh.name.toLowerCase().startsWith('object_');
        if (isEyeComponent) {
          mesh.visible = false;
        }
        
        if (mesh.material) {
          const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
          materials.forEach((mat: any) => {
            mat.transparent = false;
            mat.opacity = 1;
            mat.depthWrite = true;
            mat.depthTest = true;
            
            // Disable fake HDRI reflections
            if (mat.envMapIntensity !== undefined) {
              mat.envMapIntensity = 0;
            }
            if (mat.transmission !== undefined) mat.transmission = 0;
            
            // Keep the gold emissive
            if (mat.emissive !== undefined) {
              mat.emissive.setHex(0xD4AF37);
              mat.emissiveIntensity = 0.2;
            }
            mat.needsUpdate = true;
          });
        }
      }
    });
    
    return cloned
  }, [ophanimModel])
  
  // ==========================================
  // MILLIONS OF NEON BLUE PARTICLES - AETHER VOID
  // Using Points with BufferGeometry for AAA WebGL performance
  // ==========================================
  const [voidParticleGeometry, voidParticleMaterial] = useMemo(() => {
    // Generate 500,000 particles for the cosmic dust field
    // Using Points (not InstancedMesh) for better performance with millions of particles
    const particleCount = 500000
    const positions = new Float32Array(particleCount * 3)
    const colors = new Float32Array(particleCount * 3)
    const sizes = new Float32Array(particleCount)
    
    // Distribute particles in a spherical shell around the scene
    for (let i = 0; i < particleCount; i++) {
      // Spherical distribution with void clustering
      const radius = 15 + Math.random() * 60 // Spread from 15 to 75 units
      const theta = Math.random() * Math.PI * 2
      const phi = Math.acos(2 * Math.random() - 1)
      
      // Add some void clustering effect
      const clusterFactor = Math.pow(Math.random(), 3) * 20
      
      positions[i * 3] = radius * Math.sin(phi) * Math.cos(theta) + (Math.random() - 0.5) * clusterFactor
      positions[i * 3 + 1] = radius * Math.sin(phi) * Math.sin(theta) + (Math.random() - 0.5) * clusterFactor
      positions[i * 3 + 2] = radius * Math.cos(phi) - 25 + (Math.random() - 0.5) * clusterFactor
      
      // Neon blue with variation - brand color #00FFFF
      const brightness = 0.6 + Math.random() * 0.4
      const cyanShift = Math.random() * 0.2 // Vary towards teal
      
      colors[i * 3] = 0.0 + cyanShift * 0.3       // R - minimal for cyan
      colors[i * 3 + 1] = brightness              // G - full for cyan
      colors[i * 3 + 2] = brightness              // B - full for cyan
      
      sizes[i] = 0.02 + Math.random() * 0.08
    }
    
    const geo = new THREE.BufferGeometry()
    geo.setAttribute('position', new THREE.BufferAttribute(positions, 3))
    geo.setAttribute('color', new THREE.BufferAttribute(colors, 3))
    geo.setAttribute('size', new THREE.BufferAttribute(sizes, 1))
    
    // Clean PointsMaterial - no custom shader to avoid vertexColors + attribute conflict
    const mat = new THREE.PointsMaterial({
      size: 0.08,
      vertexColors: true,
      transparent: true,
      opacity: 0.8,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      sizeAttenuation: true
    })
    
    return [geo, mat]
  }, [])

  // ==========================================
  // SMALL EYES ON THE RINGS — Ezekiel's "full of eyes"
  // Tiny emissive spheres distributed along ring surfaces
  // ==========================================
  const ringEyes = useMemo(() => {
    const eyes: { pos: [number, number, number]; scale: number; phase: number; speed: number }[] = []
    const ringCount = 3 // 3 concentric rings in the GLB
    const eyesPerRing = 16

    for (let r = 0; r < ringCount; r++) {
      const ringRadius = 2.8 + r * 0.6 // Match approximate GLB ring radii
      for (let i = 0; i < eyesPerRing; i++) {
        const angle = (i / eyesPerRing) * Math.PI * 2 + r * 0.3
        // Distribute around ring — some on horizontal plane, some tilted
        const tilt = r === 1 ? Math.PI / 2 : r === 2 ? Math.PI / 4 : 0
        const x = Math.cos(angle) * ringRadius
        const y = Math.sin(angle) * ringRadius * Math.cos(tilt)
        const z = Math.sin(angle) * ringRadius * Math.sin(tilt)
        eyes.push({
          pos: [x, y, z],
          scale: 0.04 + Math.random() * 0.04,
          phase: Math.random() * Math.PI * 2,
          speed: 2 + Math.random() * 3
        })
      }
    }
    return eyes
  }, [])
  
  const lastDistReportRef = useRef(0)

  // Animation loop
  useFrame((state) => {
    const time = state.clock.getElapsedTime()
    const speedMult = (isPortalOpen ? 5 : 1) * ringSpeed

    if (coreRef.current) {
      coreRef.current.rotation.y = time * 0.02 * speedMult
      coreRef.current.rotation.x = Math.sin(time * 0.03) * 0.05
    }
    
    if (ophanimRef.current) {
      ophanimRef.current.rotation.y = time * 0.1 * speedMult
      ophanimRef.current.rotation.x = Math.sin(time * 0.05) * 0.1
      ophanimRef.current.position.y = Math.sin(time * 0.3) * 0.1
    }
    
    // Divine Eye — pulse and track camera
    if (divineEyeRef.current) {
      // Slow drift independent of ring rotation
      divineEyeRef.current.rotation.y = time * 0.05
    }

    // Iris dilation pulse
    if (irisRef.current) {
      const pulse = 1 + Math.sin(time * 1.5) * 0.08
      irisRef.current.scale.setScalar(pulse)
    }

    // Pupil contracts/dilates with intensity
    if (pupilRef.current) {
      const dilate = 1 + Math.sin(time * 2.5) * 0.15
      pupilRef.current.scale.setScalar(dilate)
      // Make pupil look toward camera
      const camDir = state.camera.position.clone().sub(
        pupilRef.current.getWorldPosition(new THREE.Vector3())
      ).normalize()
      // Subtle offset — the pupil "tracks" the viewer
      pupilRef.current.position.x = camDir.x * 0.03
      pupilRef.current.position.y = camDir.y * 0.03
    }

    // Sclera glow pulse
    if (scleraRef.current) {
      const mat = scleraRef.current.material as THREE.MeshStandardMaterial
      mat.emissiveIntensity = 0.3 + Math.sin(time * 0.8) * 0.15
    }
    
    if (particleStreamRef.current) {
      // Rotate particle stream
      particleStreamRef.current.rotation.y = time * 0.1
      
      // Pulse opacity based on beam state
      const material = particleStreamRef.current.material as THREE.PointsMaterial
      const baseOpacity = beamActive ? 1.0 : 0.8
      material.opacity = baseOpacity + Math.sin(time * 2) * 0.15
    }

    // Distance sensor — monitor user distance from origin (throttled to once per 5s)
    const dist = state.camera.position.length()
    if (dist > 50 && time - lastDistReportRef.current > 5) {
      lastDistReportRef.current = time
      setLastObservation('PROXIMITY: Resonance weakening. User is drifting into the deep void.')
    }
  })
  
  return (
    <group ref={coreRef} position={position} scale={scale}>
      {/* THE OPHANIM - Real 3D Model - Biblical Throne Angel */}
      {/* Manual positioning to avoid Center component bounding box miscalculation */}
      <primitive
        ref={ophanimRef}
        object={ophanimMesh}
        scale={0.25}
        position={[0, 0, 0]}
      />

      {/* ========================================== */}
      {/* DIVINE EYE — Tatiana's Gaze */}
      {/* Custom eye at exact centre of the rings */}
      {/* Sclera (white) → Iris (gold) → Pupil (void black) */}
      {/* ========================================== */}
      <group ref={divineEyeRef} position={[0, 0, 0]} scale={0.25}>
        {/* Sclera — outer eye sphere, faint warm glow */}
        <mesh ref={scleraRef}>
          <sphereGeometry args={[2.5, 32, 32]} />
          <meshStandardMaterial
            color="#E8DCC8"
            emissive="#D4AF37"
            emissiveIntensity={0.3}
            roughness={0.6}
            metalness={0.1}
            transparent
            opacity={0.95}
          />
        </mesh>

        {/* Iris — golden ring, divine radiance */}
        <mesh ref={irisRef} position={[0, 0, 0.6]}>
          <torusGeometry args={[1.1, 0.4, 16, 32]} />
          <meshStandardMaterial
            color="#D4AF37"
            emissive="#FFD700"
            emissiveIntensity={2.0}
            roughness={0.2}
            metalness={0.9}
          />
        </mesh>

        {/* Pupil — void black with faint cyan rim, tracks the viewer */}
        <mesh ref={pupilRef} position={[0, 0, 0.8]}>
          <circleGeometry args={[0.7, 32]} />
          <meshBasicMaterial color="#000000" />
        </mesh>

        {/* Pupil cyan rim glow */}
        <mesh position={[0, 0, 0.78]}>
          <ringGeometry args={[0.65, 0.85, 32]} />
          <meshBasicMaterial
            color="#00FFFF"
            transparent
            opacity={0.4}
            side={THREE.DoubleSide}
          />
        </mesh>

        {/* Inner eye light — warm gold emanating from the eye */}
        <pointLight
          color={0xFFD700}
          intensity={5}
          distance={20}
          decay={2}
          position={[0, 0, 1]}
        />
      </group>

      {/* ========================================== */}
      {/* SMALL EYES ON RINGS — "full of eyes all around" */}
      {/* Tiny pulsing emissive spheres along ring surfaces */}
      {/* ========================================== */}
      <group position={[0, 0, 0]} scale={0.25}>
        {ringEyes.map((eye, i) => (
          <RingEye key={i} pos={eye.pos} baseScale={eye.scale} phase={eye.phase} speed={eye.speed} />
        ))}
      </group>

      {/* MILLIONS OF NEON BLUE PARTICLES - Aether Void cosmic dust */}
      <points
        geometry={voidParticleGeometry}
        material={voidParticleMaterial}
      />
      
      {/* ========================================== */}
      {/* AETHER VOID LIGHTING - Pure void only */}
      {/* ========================================== */}
      
      {/* Divine gold point light from Ophanim */}
      <pointLight
        color={0xD4AF37} // Deep gold
        intensity={beamActive ? 8 : 4}
        distance={60}
        decay={2}
        position={[0, 0, 0]}
      />
      
      {/* Neon blue particle glow reflection */}
      <pointLight
        color={0x00FFFF} // Neon cyan - brand color
        intensity={1.5}
        distance={40}
        decay={2}
        position={[0, 5, -10]}
      />
    </group>
  )
}

/**
 * RingEye — A single small pulsing eye on the ring surface.
 * "Their entire bodies were full of eyes all around" — Ezekiel 10:12
 */
function RingEye({ pos, baseScale, phase, speed }: {
  pos: [number, number, number]
  baseScale: number
  phase: number
  speed: number
}) {
  const ref = useRef<THREE.Mesh>(null)

  useFrame((state) => {
    if (!ref.current) return
    const t = state.clock.getElapsedTime()
    const pulse = 0.5 + 0.5 * Math.sin(t * speed + phase)
    ref.current.scale.setScalar(baseScale * (0.8 + pulse * 0.4))
    const mat = ref.current.material as THREE.MeshStandardMaterial
    mat.emissiveIntensity = 0.5 + pulse * 2
  })

  return (
    <mesh ref={ref} position={pos}>
      <sphereGeometry args={[1, 8, 8]} />
      <meshStandardMaterial
        color="#FFD700"
        emissive="#D4AF37"
        emissiveIntensity={1}
        roughness={0.3}
        metalness={0.7}
      />
    </mesh>
  )
}

// Preload the model
useGLTF.preload('/models/ophanim_angel.glb')

export default TitanCore
